/**
 * CSuitePreviewPopover — hover-reveal card that itemises what the Executive
 * Portal contains. Wraps a trigger element; the popover appears on
 * mouse-enter (with a small enter delay) and disappears on leave, but stays
 * open while the cursor is on the popover itself. On touch devices it toggles
 * on tap. Themed to match the C-Suite cherry palette.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  Layers, Target, BarChart3, TrendingUp, Sparkles, Zap, FileText, Plug,
} from "lucide-react";

const MODULES = [
  { icon: Layers,     name: "Portfolio Rollup",       desc: "Every project's RAG, budget, risks · one screen" },
  { icon: Target,     name: "Business-Unit Scorecard","desc": "5-dimension weighted RAG across BUs" },
  { icon: BarChart3,  name: "S/4HANA Cockpit",         desc: "Close days · OTD % · module adoption · waves" },
  { icon: BarChart3,  name: "Dynamics 365 Cockpit",    desc: "F&O + Sales KPIs · pipeline · regional revenue" },
  { icon: TrendingUp, name: "Value Realization",       desc: "Planned vs achieved benefits by owner" },
  { icon: Sparkles,   name: "AI Insights",              desc: "Claude 4.5 daily executive briefings" },
  { icon: Zap,        name: "NAVBOT (Claude 4.5)",     desc: "Executive AI — activates the moment you pay" },
  { icon: FileText,   name: "Board Pack (20 slides)",  desc: "One-click PPTX from live portfolio metrics" },
  { icon: Plug,       name: "Live Connections",         desc: "SAP + D365 OAuth to swap mock for real" },
];

// Correct the accidental quote-in-value from the array (JSX-safe).
MODULES[1].desc = "5-dimension weighted RAG across BUs";

export function CSuitePreviewPopover({ children, align = "right" }) {
  const [open, setOpen] = useState(false);
  const enterTimer = useRef(null);
  const leaveTimer = useRef(null);
  const wrapRef = useRef(null);

  const openLater = () => {
    clearTimeout(leaveTimer.current);
    enterTimer.current = setTimeout(() => setOpen(true), 120);
  };
  const closeLater = () => {
    clearTimeout(enterTimer.current);
    leaveTimer.current = setTimeout(() => setOpen(false), 180);
  };

  // Close on outside tap for touch users.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => () => { clearTimeout(enterTimer.current); clearTimeout(leaveTimer.current); }, []);

  const alignClass = align === "left" ? "left-0" : "right-0";

  return (
    <span
      ref={wrapRef}
      className="relative inline-block"
      onMouseEnter={openLater}
      onMouseLeave={closeLater}
      onFocus={openLater}
      onBlur={closeLater}
    >
      {/* Trigger — augment with aria + touch toggle. */}
      <span
        onClick={(e) => {
          // On touch devices (no hover), tap toggles the popover on the first
          // tap and lets the underlying click go through on the second.
          if (window.matchMedia && window.matchMedia("(hover: none)").matches && !open) {
            e.preventDefault(); e.stopPropagation(); setOpen(true);
          }
        }}
        aria-describedby="csuite-preview-popover"
      >
        {children}
      </span>

      {open && (
        <div
          id="csuite-preview-popover"
          role="dialog"
          aria-label="What's inside the C-Suite Executive Portal"
          data-testid="csuite-preview-popover"
          className={`absolute z-50 bottom-full mb-3 ${alignClass} w-[360px] anim-in`}
          style={{ animationDuration: "180ms" }}
          onMouseEnter={openLater}
          onMouseLeave={closeLater}
        >
          <div
            className="rounded-md shadow-2xl overflow-hidden border"
            style={{
              background: "linear-gradient(180deg, #FDFBFB 0%, #FDF2F4 100%)",
              borderColor: "#F0D3D8",
            }}
          >
            {/* Cherry header */}
            <div className="px-4 py-3" style={{ background: "linear-gradient(135deg, #4A0F1A 0%, #7A1F2B 100%)" }}>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: "#E5A9B3" }}>
                Executive Portal · $350/mo
              </div>
              <div className="font-display text-[15px] font-extrabold mt-0.5" style={{ color: "#FFFFFF" }}>
                What's inside for CIOs &amp; CFOs
              </div>
            </div>

            {/* Module list */}
            <ul className="p-2">
              {MODULES.map((m, i) => (
                <li
                  key={m.name}
                  className="flex items-start gap-2.5 px-2.5 py-1.5 rounded-sm hover:bg-white transition-colors"
                  data-testid={`csuite-preview-item-${i}`}
                >
                  <span
                    className="w-6 h-6 rounded-sm grid place-items-center shrink-0 mt-0.5"
                    style={{ background: "#7A1F2B", color: "#FFFFFF" }}
                  >
                    <m.icon size={12} strokeWidth={2.2} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[12px] font-semibold" style={{ color: "#2A0A10" }}>{m.name}</span>
                    <span className="block text-[11px] leading-tight" style={{ color: "#5C1F2A" }}>{m.desc}</span>
                  </span>
                </li>
              ))}
            </ul>

            {/* Footer */}
            <div className="px-4 py-2.5 border-t flex items-center justify-between"
                 style={{ borderColor: "#F0D3D8", background: "#FDF2F4" }}>
              <span className="font-mono text-[9px] uppercase tracking-[0.16em]" style={{ color: "#7A1F2B" }}>
                14-day pilot at $0
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.16em]" style={{ color: "#8A6E75" }}>
                Click to sign up →
              </span>
            </div>
          </div>

          {/* Chevron notch */}
          <span
            className={`absolute -bottom-1.5 ${align === "left" ? "left-5" : "right-5"} w-3 h-3 rotate-45`}
            style={{ background: "#FDF2F4", borderRight: "1px solid #F0D3D8", borderBottom: "1px solid #F0D3D8" }}
          />
        </div>
      )}
    </span>
  );
}
